import React from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { Cpu, Lock, Zap, Globe, Shield, Network, Building, Flame, CheckCircle, XCircle } from 'lucide-react';

export const QFS = () => {
  const features = [
    {
      icon: Lock,
      title: 'Quantum-Level Security',
      description: 'Utilizing quantum encryption technology to protect financial transactions from current and future threats.',
    },
    {
      icon: Zap,
      title: 'Instant Settlements',
      description: 'Real-time transaction processing and instant global settlement capabilities.',
    },
    {
      icon: Globe,
      title: 'Global Infrastructure',
      description: 'Seamlessly connected worldwide financial network infrastructure.',
    },
    {
      icon: Shield,
      title: 'Asset Protection',
      description: 'Advanced protocols ensuring complete protection of digital and physical assets.',
    },
    {
      icon: Network,
      title: 'Decentralized Network',
      description: 'Distributed ledger technology ensuring transparency and reliability.',
    },
    {
      icon: Cpu,
      title: 'AI-Powered Operations',
      description: 'Intelligent automation and predictive analytics for optimal performance.',
    },
  ];

  const comparisonPoints = [
    {
      metric: 'Transaction Speed',
      traditional: '2-5 business days',
      qfs: 'Instant (seconds)',
      traditionalBad: true,
    },
    {
      metric: 'Security Level',
      traditional: 'Classical encryption',
      qfs: 'Quantum encryption',
      traditionalBad: true,
    },
    {
      metric: 'Processing Fees',
      traditional: 'High fees & hidden charges',
      qfs: 'Minimal to zero fees',
      traditionalBad: true,
    },
    {
      metric: 'Global Accessibility',
      traditional: 'Limited by borders',
      qfs: 'Truly global & borderless',
      traditionalBad: true,
    },
    {
      metric: 'Transparency',
      traditional: 'Opaque & centralized',
      qfs: 'Fully transparent & decentralized',
      traditionalBad: true,
    },
    {
      metric: 'System Resilience',
      traditional: 'Vulnerable to failures',
      qfs: 'Quantum-resistant & robust',
      traditionalBad: true,
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1666816943035-15c29931e975?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxibG9ja2NoYWluJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzZ8MA&ixlib=rb-4.1.0&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Quantum Financial System
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Next-generation financial infrastructure powered by quantum technology
          </p>
        </div>
      </section>

      {/* Comparison Section - Traditional Banking Burning */}
      <AnimatedSection>
        <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
          {/* Burning effect background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-gradient-to-t from-red-600 via-orange-500 to-yellow-400 animate-pulse"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Traditional Banking vs QFS
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                The old system is burning as the new quantum era rises
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Traditional Banking Card - Burning Effect */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 animate-pulse"></div>
                <Card className="relative h-full bg-gray-800 border-2 border-red-500 shadow-2xl overflow-hidden">
                  {/* Fire overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-t from-red-600/40 via-orange-500/30 to-yellow-400/20 animate-pulse"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-red-600 to-transparent animate-pulse"></div>
                  </div>
                  
                  <CardContent className="p-8 relative">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                          <Building className="w-6 h-6 text-red-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white">Traditional Banking</h3>
                      </div>
                      <Flame className="w-8 h-8 text-orange-500 animate-pulse" />
                    </div>
                    
                    {/* Burning bank image - ATM image */}
                   {/* Burning bank image */}
<div className="relative mb-8 rounded-xl overflow-hidden">
  <img
    src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?crop=entropy&cs=srgb&fm=jpg&q=85"
    alt="Bank building collapsing system"
    className="w-full h-64 object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
  />

  {/* Fire glow overlay */}
  <div className="absolute inset-0 bg-gradient-to-t from-red-700/70 via-orange-500/50 to-yellow-400/20 animate-pulse"></div>

  {/* Smoke dark layer */}
  <div className="absolute inset-0 bg-black/40 mix-blend-multiply"></div>

  {/* Bottom flames effect */}
  <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-red-600 via-orange-500 to-transparent animate-pulse"></div>

  {/* Warning text */}
  <div className="absolute bottom-4 left-4 right-4">
    <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3">
      <p className="text-red-400 text-sm font-semibold">🔥 SYSTEM COLLAPSE 🔥</p>
      <p className="text-yellow-400 text-xs mt-1">
        Traditional Banking Failing • Outdated Infrastructure • High Fees
      </p>
    </div>
  </div>
</div>
                    
                    <div className="space-y-4">
                      {comparisonPoints.map((point, idx) => (
                        <div key={idx} className="flex items-center justify-between border-b border-gray-700 pb-3">
                          <span className="text-gray-300 font-medium">{point.metric}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-red-400 line-through">{point.traditional}</span>
                            <XCircle className="w-5 h-5 text-red-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Fire particles effect */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-400 animate-pulse"></div>
                  </CardContent>
                </Card>
              </div>

              {/* QFS Banking Card - Rising from Ashes */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#FFD700] via-[#FFE55C] to-[#FFD700] rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 animate-pulse"></div>
                <Card className="relative h-full bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] border-2 border-[#FFD700] shadow-2xl transform hover:scale-105 transition-all duration-500">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-12 h-12 bg-[#FFD700]/20 rounded-xl flex items-center justify-center">
                        <Shield className="w-6 h-6 text-[#FFD700]" />
                      </div>
                      <h3 className="text-2xl font-bold text-white">Quantum Financial System</h3>
                    </div>
                    
                    {/* QFS rising image */}
                    <div className="relative mb-8 rounded-xl overflow-hidden">
                      <img
                        src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxxdWFudHVtJTIwZmluYW5jZSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzQyNTY5NjAwfDA&ixlib=rb-4.1.0&q=85"
                        alt="QFS Technology"
                        className="w-full h-64 object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A]/50 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3">
                          <p className="text-[#FFD700] text-sm font-semibold">✨ QUANTUM ERA RISING ✨</p>
                          <p className="text-gray-300 text-xs mt-1">Instant Global Transactions • Quantum Security • Zero Fees</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {comparisonPoints.map((point, idx) => (
                        <div key={idx} className="flex items-center justify-between border-b border-[#FFD700]/20 pb-3">
                          <span className="text-gray-300 font-medium">{point.metric}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#FFD700] font-bold">{point.qfs}</span>
                            <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-8 text-center">
                      <div className="inline-block bg-[#FFD700]/10 rounded-full px-6 py-2">
                        <span className="text-[#FFD700] font-semibold">100x More Efficient</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Main Content */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  The Future of Secure Global Transactions
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  The Quantum Financial System (QFS) represents a theoretical next-generation financial infrastructure designed to revolutionize how global transactions are processed and secured.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Built on principles of quantum computing and advanced cryptography, QFS aims to create an unprecedented level of security and efficiency in financial operations. This system explores the potential of quantum technology to address current limitations in traditional banking infrastructure.
                </p>  
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                 Well the main fact here is that the banking system is crashing globally, because the bank crises have been unsustainable macroeconomic policies (including large  current account deficits and unsustainable public debt), excessive credit booms, large capital inflows, and balance sheet fragilities, combined with policy paralysis due to a variety of political and economic, you have to backup your assets immediately because Banks, stock market, and Exchanges are crashing down and all assets will be lost in unexpected time.

                </p>
              
                <p className="text-lg text-gray-600 leading-relaxed">
                  By leveraging quantum entanglement and superposition, the QFS concept proposes a financial network that is virtually unhackable while maintaining lightning-fast transaction speeds across global networks.
                </p>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We’ve dealt with invisible deductions and banks loaning out our money for their own profit for too long. The transition to a Quantum-based system is about ending that cycle. Between the Quantum programs and the restoration of stolen funds, the focus shifts entirely to the individual's growth and security. It’s a system built for the people, not the institutions. What do you think about that move toward financial sovereignty?

                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxibG9ja2NoYWluJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Quantum Financial Technology"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FFD700] rounded-full filter blur-3xl opacity-20" />
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Features Grid */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Key Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Advanced capabilities powering the quantum financial infrastructure
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <feature.icon className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Technology Section */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                <img
                  src="https://images.unsplash.com/photo-1707075891545-41b982930351?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxmaW50ZWNoJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzM3MzU2NzB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Quantum Technology"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#0B2A4A] rounded-full filter blur-3xl opacity-10" />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Revolutionary Technology
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  The Quantum Financial System explores cutting-edge technologies that could reshape global financial infrastructure:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Quantum Encryption:</strong> Leveraging quantum mechanics for unbreakable security protocols
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Distributed Ledger:</strong> Transparent and immutable transaction records
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">AI Integration:</strong> Intelligent systems for fraud detection and optimization
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Global Connectivity:</strong> Seamless cross-border transaction capabilities
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default QFS;